import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';

type Snapshot = Array<readonly [QueryKey, unknown]>;

/**
 * The standard TanStack Query optimistic flow, factored out so every mutation
 * gets it consistently. Spread the result into `useMutation` next to `mutationFn`:
 *
 *   useMutation({
 *     mutationFn: ...,
 *     ...optimistic<Vars>({
 *       qc,
 *       keys: () => [queryKeys.trips.items(tripId)],   // snapshot + reconcile
 *       apply: (vars) => { ...write the optimistic change into the cache... },
 *       errorMessage: 'Couldn’t save that change',
 *       also: () => [queryKeys.trips.all],             // extra keys to invalidate
 *     }),
 *   });
 *
 * - onMutate: cancels in-flight refetches for `keys` (so they can't clobber the
 *   optimistic value), snapshots them, applies the change, returns the snapshot.
 * - onError: restores the snapshot and shows `errorMessage`.
 * - onSettled: invalidates `keys` + `also` to reconcile with the server — but
 *   only once no other mutation is still in flight. Otherwise a refetch kicked
 *   off by the first of two quick edits (check, then uncheck) can land while the
 *   second is still pending and briefly show the server's in-between state; the
 *   invalidations are queued and flushed when the last mutation settles.
 */
export function optimistic<TVars>(opts: {
  qc: QueryClient;
  keys: (vars: TVars) => QueryKey[];
  apply: (vars: TVars) => void;
  errorMessage: string;
  also?: (vars: TVars) => QueryKey[];
  /**
   * Runs after the snapshot is restored. Return true to say the error was
   * handled (no generic toast) — e.g. a 409 the UI resolves inline.
   */
  handleError?: (error: unknown, vars: TVars) => boolean;
}) {
  return {
    onMutate: async (vars: TVars) => {
      const keys = opts.keys(vars);
      await Promise.all(keys.map((key) => opts.qc.cancelQueries({ queryKey: key })));
      const snapshot: Snapshot = keys.map((key) => [key, opts.qc.getQueryData(key)] as const);
      opts.apply(vars);
      return { snapshot };
    },
    onError: (error: unknown, vars: TVars, context: { snapshot: Snapshot } | undefined) => {
      for (const [key, data] of context?.snapshot ?? []) {
        opts.qc.setQueryData(key, data);
      }
      if (opts.handleError?.(error, vars)) {
        return;
      }
      toast.error(opts.errorMessage);
    },
    onSettled: (_data: unknown, _error: unknown, vars: TVars) => {
      for (const key of [...opts.keys(vars), ...(opts.also?.(vars) ?? [])]) {
        deferredInvalidations.set(JSON.stringify(key), key);
      }
      // This mutation still counts as pending inside its own onSettled, so >1
      // means someone else is mid-flight; they'll flush when they settle.
      if (opts.qc.isMutating() > 1) {
        return;
      }
      flushDeferred(opts.qc);
    },
  };
}

/** Invalidations held back while other mutations were still in flight. */
const deferredInvalidations = new Map<string, QueryKey>();

function flushDeferred(qc: QueryClient): void {
  const keys = [...deferredInvalidations.values()];
  deferredInvalidations.clear();
  for (const key of keys) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

/**
 * Invalidate `keys` — now if nothing is mid-flight, otherwise once the last
 * pending mutation settles. The live stream uses this for other people's
 * changes: a refetch that lands while one of the user's own edits is still
 * queued would show the server's pre-edit state and "revert" the item under
 * their thumb. Sharing the deferral map with `optimistic()` means the last
 * mutation's own onSettled flushes whatever was held.
 */
export function scheduleInvalidation(qc: QueryClient, keys: QueryKey[]): void {
  for (const key of keys) {
    deferredInvalidations.set(JSON.stringify(key), key);
  }
  if (qc.isMutating() === 0) {
    flushDeferred(qc);
  }
}

const chains = new Map<string, Promise<unknown>>();

/**
 * Run mutation requests for one key (a trip) strictly one after another, in the
 * order they were started. The optimistic cache update still happens the moment
 * the user acts; only the network calls queue. Without this, a quick check →
 * uncheck sends two overlapping PATCHes and whichever the server commits last
 * wins — which can be the first one, silently reverting the item.
 */
export function inOrder<F extends (vars: never) => Promise<unknown>>(key: string, fn: F): F {
  const wrapped = (vars: Parameters<F>[0]): Promise<unknown> => {
    const previous = chains.get(key) ?? Promise.resolve();
    const run = previous.then(
      () => fn(vars),
      () => fn(vars),
    );
    // Keep the chain alive past failures; each caller still sees its own error.
    const tail = run.then(
      () => undefined,
      () => undefined,
    );
    chains.set(key, tail);
    void tail.then(() => {
      if (chains.get(key) === tail) {
        chains.delete(key);
      }
    });
    return run;
  };
  return wrapped as F;
}

/** A client-only temp id for optimistic creates. The real row replaces it on settle. */
export function tempId(): string {
  return `optimistic-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/** True once a row has a server-assigned id (i.e. not an optimistic placeholder). */
export function isPersistedId(id: string): boolean {
  return !id.startsWith('optimistic-');
}
